import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Wand2, Loader2, Copy } from "lucide-react";
import { api } from "@/lib/api";
import { apiError } from "@/lib/campus";

const KEYS = [["instagram", "Instagram"], ["tiktok", "TikTok"], ["youtube", "YouTube"], ["x", "X (Twitter)"], ["facebook", "Facebook"], ["linkedin", "LinkedIn"]];
const field = "w-full bg-[#0B0E14] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-amber-500";
const copy = (t) => navigator.clipboard?.writeText(t).then(() => toast.success("Copied"));

const KitView = ({ kit }) => (
  <div className="space-y-4" data-testid="launch-kit-result">
    <div className="rounded-xl bg-[#121721] border border-white/10 p-4">
      <div className="text-xs text-slate-500 mb-1">Handles to claim on every platform</div>
      <div className="flex flex-wrap gap-2">{(kit.handles || []).map((h) => <span key={h} className="text-sm text-amber-300 bg-amber-500/10 px-2.5 py-1 rounded-full">{h}</span>)}</div>
    </div>
    {[["Short bio", kit.bio_short], ["Long bio", kit.bio_long], ["Hashtags", (kit.hashtags || []).join(" ")]].map(([k, v]) => (
      <div key={k} className="rounded-xl bg-[#121721] border border-white/10 p-4">
        <div className="flex justify-between text-xs text-slate-500 mb-1">{k}<button onClick={() => copy(v)} className="hover:text-amber-300"><Copy className="h-3.5 w-3.5" /></button></div>
        <p className="text-sm text-slate-200">{v}</p>
      </div>
    ))}
    <div className="grid md:grid-cols-2 gap-3">
      {(kit.posts || []).map((p, i) => (
        <div key={i} className="rounded-xl bg-[#121721] border border-white/10 p-4" data-testid={`launch-post-${i}`}>
          <div className="flex justify-between text-xs text-amber-400 mb-1">{p.platform}<button onClick={() => copy(p.text)} className="text-slate-500 hover:text-amber-300"><Copy className="h-3.5 w-3.5" /></button></div>
          <p className="text-sm text-slate-200 whitespace-pre-line">{p.text}</p>
        </div>
      ))}
    </div>
  </div>
);

export const LaunchPanel = () => {
  const [links, setLinks] = useState({});
  const [kit, setKit] = useState(null);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/site/social").then(({ data }) => setLinks(data)).catch(() => {});
    api.get("/admin/launch-kit").then(({ data }) => setKit(data)).catch(() => {});
  }, []);

  const save = async () => {
    try { await api.put("/admin/site/social", { links }); toast.success("Social links saved — they show in the site footer"); }
    catch (err) { toast.error(apiError(err, "Couldn't save")); }
  };
  const generate = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/launch-kit", { notes }); setKit(data); toast.success("Launch kit ready"); }
    catch (err) { toast.error(apiError(err, "Couldn't generate")); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-8" data-testid="launch-panel">
      <section>
        <h3 className="text-white font-semibold mb-1">School social pages</h3>
        <p className="text-xs text-slate-500 mb-3">Create the accounts yourself (they need your phone/ID), then paste the profile links here. Only filled ones appear in the footer.</p>
        <div className="grid md:grid-cols-2 gap-3">
          {KEYS.map(([k, label]) => (
            <div key={k}>
              <label className="text-xs text-slate-400">{label}</label>
              <input data-testid={`social-${k}`} value={links[k] || ""} placeholder="https://…" onChange={(e) => setLinks({ ...links, [k]: e.target.value })} className={field} />
            </div>
          ))}
        </div>
        <button data-testid="social-save" onClick={save} className="mt-3 bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-sm px-5 py-2.5 rounded-lg">Save links</button>
      </section>
      <section>
        <h3 className="text-white font-semibold mb-1">AI launch kit</h3>
        <p className="text-xs text-slate-500 mb-3">Handles to claim, bios and ready-to-post launch posts.</p>
        <div className="flex gap-2 mb-5">
          <input data-testid="launch-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional: tone, audience, launch date…" className={field} />
          <button data-testid="launch-generate" onClick={generate} disabled={busy} className="shrink-0 flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-sm px-4 rounded-lg disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />} Generate
          </button>
        </div>
        {kit && <KitView kit={kit} />}
      </section>
    </div>
  );
};
